import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { teachersService } from '../../services/teachers';

function ImpactTable({ columns, rows, empty }) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {empty}
      </Typography>
    );
  }
  return (
    <Box sx={{ maxHeight: 220, overflow: 'auto', mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={row.id || `${row.batch_name || ''}-${idx}`}>
              {columns.map((col) => (
                <TableCell key={col.key}>{row[col.key] ?? '—'}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function TeacherDeactivateDialog({ open, teacher, onClose, onDeactivated }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [impact, setImpact] = useState(null);
  const [leaveBatchesEmpty, setLeaveBatchesEmpty] = useState(false);
  const [unassignDemos, setUnassignDemos] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    if (!open || !teacher?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setImpact(null);
    setLeaveBatchesEmpty(false);
    setUnassignDemos(false);
    setReviewed(false);
    teachersService
      .getDeactivationImpact(teacher.id)
      .then((res) => {
        if (cancelled) return;
        setImpact(res?.data || res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Failed to load what will be removed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, teacher?.id]);

  const data = impact?.teacher ? impact : impact?.data || impact;
  const livePrimary = data?.live_primary_batches || [];
  const liveSecondary = data?.live_secondary_batches || [];
  const futureDemos = data?.future_demos || [];
  const futureCovers = data?.future_covers || [];
  const futureLeave = data?.future_leave_substitutes || [];
  const meetings = data?.upcoming_meetings || [];
  const threads = data?.open_parent_threads || [];
  const futureSessions = data?.future_sessions || [];
  const name = data?.teacher?.name || `${teacher?.first_name || ''} ${teacher?.last_name || ''}`.trim();

  const needsBatchConfirm = livePrimary.length > 0;
  const needsDemoConfirm = futureDemos.length > 0;
  const canSubmit =
    reviewed &&
    (!needsBatchConfirm || leaveBatchesEmpty) &&
    (!needsDemoConfirm || unassignDemos) &&
    !loading &&
    !submitting &&
    !!data;

  const handleConfirm = async () => {
    if (!teacher?.id || !canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await teachersService.deleteTeacher(teacher.id);
      onDeactivated?.(result);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deactivate teacher');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Deactivate {name || 'teacher'}?</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" alignItems="center" gap={1} sx={{ py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Loading live and future assignments…</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {data?.already_inactive ? (
              <Alert severity="info">This account is already inactive. Confirming will still clear leftover future assignments.</Alert>
            ) : null}
            <Typography variant="body2">
              Login will be blocked. Past classes, timesheets, reports, and completed demos stay as history.
              Only future work and open parent-teacher threads below will be removed.
            </Typography>

            <Typography variant="subtitle2">Live primary batches — will be left with no teacher ({livePrimary.length})</Typography>
            <ImpactTable
              columns={[
                { key: 'name', label: 'Batch' },
                { key: 'status', label: 'Status' },
                { key: 'country', label: 'Country' },
                { key: 'active_students', label: 'Students' },
              ]}
              rows={livePrimary}
              empty="None."
            />

            <Typography variant="subtitle2">Live secondary / backup ({liveSecondary.length})</Typography>
            <ImpactTable
              columns={[
                { key: 'name', label: 'Batch' },
                { key: 'status', label: 'Status' },
                { key: 'country', label: 'Country' },
              ]}
              rows={liveSecondary}
              empty="None."
            />

            <Typography variant="subtitle2">Future demos — teacher will be unassigned ({futureDemos.length})</Typography>
            <ImpactTable
              columns={[
                { key: 'scheduled_date', label: 'Date' },
                { key: 'scheduled_time', label: 'Time' },
                { key: 'status', label: 'Status' },
                { key: 'channel', label: 'Channel' },
              ]}
              rows={futureDemos}
              empty="None."
            />

            <Typography variant="subtitle2">Future session covers ({futureCovers.length})</Typography>
            <ImpactTable
              columns={[
                { key: 'batch_name', label: 'Batch' },
                { key: 'session_date', label: 'Date' },
                { key: 'start_time', label: 'Time' },
              ]}
              rows={futureCovers}
              empty="None."
            />

            <Typography variant="subtitle2">Future leave substitutes ({futureLeave.length})</Typography>
            <ImpactTable
              columns={[
                { key: 'batch_name', label: 'Batch' },
                { key: 'leave_date', label: 'Date' },
                { key: 'status', label: 'Status' },
              ]}
              rows={futureLeave}
              empty="None."
            />

            <Typography variant="subtitle2">Upcoming staff meetings ({meetings.length})</Typography>
            <ImpactTable
              columns={[
                { key: 'title', label: 'Meeting' },
                { key: 'meeting_date', label: 'Date' },
                { key: 'start_time', label: 'Time' },
              ]}
              rows={meetings}
              empty="None."
            />

            <Typography variant="subtitle2">Open parent-teacher threads — teacher will be detached ({threads.length})</Typography>
            <Alert severity="warning" sx={{ py: 0.5 }}>
              These conversations stay in the inbox. The teacher name is removed; staff user cannot be emptied in the database, so ops should reassign if a parent is still waiting.
            </Alert>
            <ImpactTable
              columns={[
                { key: 'subject', label: 'Subject' },
                { key: 'status', label: 'Status' },
              ]}
              rows={threads}
              empty="None."
            />

            <Typography variant="subtitle2">Future class tiles still named as this teacher ({futureSessions.length})</Typography>
            {!data?.session_teacher_id_nullable && futureSessions.length > 0 ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Session teacher cannot be blank in the database. These dates stay until a new primary is assigned.
              </Alert>
            ) : null}
            <ImpactTable
              columns={[
                { key: 'batch_name', label: 'Batch' },
                { key: 'session_date', label: 'Date' },
                { key: 'start_time', label: 'Time' },
              ]}
              rows={futureSessions}
              empty="None."
            />

            {data?.lms_assignment_count > 0 ? (
              <Typography variant="body2">
                LMS assignments that will be removed: {data.lms_assignment_count}
              </Typography>
            ) : null}

            {needsBatchConfirm ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={leaveBatchesEmpty}
                    onChange={(e) => setLeaveBatchesEmpty(e.target.checked)}
                  />
                }
                label={`Leave ${livePrimary.length} live batch${livePrimary.length === 1 ? '' : 'es'} with no teacher`}
              />
            ) : null}
            {needsDemoConfirm ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={unassignDemos}
                    onChange={(e) => setUnassignDemos(e.target.checked)}
                  />
                }
                label={`Unassign this teacher from ${futureDemos.length} future demo${futureDemos.length === 1 ? '' : 's'} (keep the demo)`}
              />
            ) : null}
            <FormControlLabel
              control={<Checkbox checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />}
              label="I have reviewed this list"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={!canSubmit}>
          {submitting ? 'Deactivating…' : data?.already_inactive ? 'Clear leftover assignments' : 'Deactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
